/**
 * EL PM 設計系統 — 常數表與小工具
 *
 * 畫面元件已經全部改用 MUI（components/mui、theme/mui.js），原本在這裡的
 * inline style 產生器（pill、按鈕、輸入框、卡片…）都已拿掉。
 * 留下來的是各頁共用的資料常數（型別、狀態、優先級、通知）、頭像配色，
 * 以及 RichView 的 sxStyle 介面還在用的 sx() 字串轉換。
 */

/* ===================== 常數表 ===================== */

/**
 * 議題型別（前 7 種為設計稿原有；initiative 為後端 issue_type 既有型別，沿用同語彙擴充）。
 *
 * label 用設計稿指定的白話名稱：大項目 / 中項目 / 小項目 / 問題修正 / 工作。
 * 舊的「史詩」「使用者故事」「子任務」是敏捷開發的行話，沒接觸過的人看不懂，
 * 而這套系統的使用者是門市與行政，不是只有 RD —— 設計稿 el-pm-template_4.html
 * 的「新增工項」下拉（L1152-L1158）就是用這五個名字。
 *
 * desc 保留術語原文，滑過去就看得到它在敏捷裡叫什麼，跟外部文件對得起來。
 *
 * 策略主題 / 技術債 / 探索 三種設計稿沒有指定新名字，維持原樣 ——
 * 自己替設計者發明譯名比留著舊詞更糟。
 *
 * c 一律是 var(--ink)：新設計的型別徽章是無彩的（.el-tag--type）——
 * 型別不是狀態，不該跟「逾期」「進行中」搶同一組顏色。
 */
export const TYPE = {
  initiative: {
    label: "策略主題",
    c: "var(--ink)",
    desc: "策略主題（Initiative）：跨專案的年度或季度目標，其下包含多個史詩",
  },
  epic: {
    label: "大項目",
    c: "var(--ink)",
    desc: "史詩（Epic）：大型功能或模組，通常需時數週至數月，其下拆分為多個使用者故事",
  },
  story: {
    label: "中項目",
    c: "var(--ink)",
    desc: "使用者故事（User Story）：從使用者角度描述的一項需求，例如「會員可使用手機號碼登入」。為需求的基本單位，完成後即可交付價值",
  },
  subtask: {
    label: "小項目",
    c: "var(--ink)",
    desc: "子任務（Sub-task）：工項下再細分的執行步驟，例如「繪製登入頁 UI」",
  },
  bug: { label: "問題修正", c: "var(--ink)", desc: "缺陷（Bug）：功能未依規格運作，需要修復" },
  tech: {
    label: "技術債",
    c: "var(--ink)",
    desc: "技術債（Tech Debt）：為趕時程而妥協的實作，日後須重新整理，延宕越久越難修改",
  },
  explore: {
    label: "探索",
    c: "var(--ink)",
    desc: "探索（Spike）：研究可行性或技術選型，產出為結論而非功能",
  },
  task: { label: "工作", c: "var(--ink)", desc: "任務（Task）：一般工作項目，不屬於上述任何類型" },
};

/** 型別徽章的 title 文案（滑過顯示白話解釋） */
export function typeTitle(t) {
  const m = TYPE[t] || TYPE.task;
  return m.desc || m.label;
}

/**
 * 工項狀態 —— 對應設計稿的 .el-tag 修飾詞：
 * 待處理/已關閉 = --closed、進行中 = --progress、審核中 = --warn、已完成 = --done。
 */
export const STATUS = {
  pending: { label: "待處理", c: "var(--closed-ink)", bg: "var(--closed-soft)" },
  progress: { label: "進行中", c: "var(--progress-ink)", bg: "var(--primary-soft)" },
  review: { label: "審核中", c: "var(--warn-ink)", bg: "var(--warn-soft)" },
  done: { label: "已完成", c: "var(--done-ink)", bg: "var(--done-soft)" },
  closed: { label: "已關閉", c: "var(--closed-ink)", bg: "var(--closed-soft)" },
};

/**
 * 優先級。
 *
 * 「緊急」改用 .el-tag--attention 的淺底深字，不再是紅底白字：
 * 同一頁若有好幾件緊急的，整排紅色色塊會把真正要看的東西蓋掉，
 * 而深色主題下 --ink-on-fill 是深藍，壓在紅底上根本看不清楚。
 */
export const PRIO = {
  urgent: { label: "緊急", c: "var(--attention-ink)", bg: "var(--attention-soft)" },
  high: { label: "高", c: "var(--warn-ink)", bg: "var(--warn-soft)" },
  medium: { label: "一般", c: "var(--closed-ink)", bg: "var(--closed-soft)" },
  low: { label: "低", c: "var(--ink-muted)", bg: "var(--closed-soft)" },
};

/** 通知類型色 */
export const NOTIFC = {
  review_requested: "var(--warn-ink)",
  /* 紫色在新設計沒有對應色，退成中性 */
  mentioned: "var(--ink-muted)",
  overdue: "var(--attention)",
  status_change: "var(--progress-ink)",
  task_assigned: "var(--done)",
};

/** 通知類型 → 徽章單字 */
export const NOTIF_ICON = {
  review_requested: "審",
  mentioned: "@",
  overdue: "逾",
  status_change: "狀",
  task_assigned: "派",
  commented: "留",
  review_approved: "准",
  review_changes_requested: "退",
  due_soon: "期",
};

/**
 * 頭像底色（依姓名穩定配色）。
 *
 * 這一組刻意留著字面色，不接 token：頭像的顏色是「這是誰」的識別，
 * 不是狀態語意。全部換成 token 只會讓所有人的頭像變成同一個藍，
 * 反而看不出誰是誰。深淺色下都夠深，白字壓得住。
 */
const AVATAR_PALETTE = [
  "#0e7c66",
  "#7c5cff",
  "#2563eb",
  "#dc6803",
  "#d94b8f",
  "#0891b2",
  "#d92d20",
  "#8a94a3",
];

/** 設計稿內建的固定配色，其餘依姓名雜湊 */
export const AVATAR = {
  黃子銘: "#0e7c66",
  楊承芳: "#7c5cff",
  張哲瑋: "#2563eb",
  王子瑜: "#dc6803",
  楊鎧睿: "#d94b8f",
  吳曉芬: "#0891b2",
};

/** 依姓名取得穩定頭像色 */
export function avatarColor(name) {
  if (!name) return "#8a94a3";
  if (AVATAR[name]) return AVATAR[name];
  let h = 0;
  for (let i = 0; i < name.length; i += 1) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
}

/** 取姓名首字（頭像文字） */
export function initialOf(name) {
  return name ? String(name).charAt(0) : "?";
}

/** 圖表畫布尺寸（設計稿固定值） */
export const chW = 520;
export const chH = 170;

/* ===================== 工具 ===================== */

/**
 * 把設計稿的 inline style 字串轉成 React style 物件。
 * 設計稿全部以字串描述樣式，這個函式讓我們能逐字沿用而不需手動改寫。
 */
export function sx(str) {
  if (!str) return {};
  if (typeof str === "object") return str;
  const out = {};
  String(str)
    .split(";")
    .forEach((part) => {
      const i = part.indexOf(":");
      if (i < 0) return;
      const key = part.slice(0, i).trim();
      const val = part.slice(i + 1).trim();
      if (!key || !val) return;
      const camel = key.startsWith("--")
        ? key
        : key.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
      out[camel] = val;
    });
  return out;
}

